// @flow
import { useCallback, useEffect, useRef, useState, type Node } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { COLORS, SPACING } from '@tablekit/theme';
import { animated, useSprings, useSpring } from 'react-spring/web.cjs';
import { useGesture } from 'react-with-gesture';
import throttle from 'lodash/throttle';
import Title from 'styled/Title';
import columnWidth from 'utils/columnWidth';
import type { BlokComponentType } from 'components/types';
import { getSprings, type CardDisplaysType } from './getSprings';

type PropsType = {
  cardDisplays: CardDisplaysType,
  cards: any[],
  springsRenderer: (springs: *[], cards: BlokComponentType<any>[]) => Node,
  title?: ?string
};

export const carouselId = 'carousel-items-parent';

const Outer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: ${columnWidth(11)}%;
  flex: 1 1 auto;
  margin-bottom: ${SPACING.L6};

  @media (min-width: 576px) {
    width: 100%;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex: 1 1 auto;
  position: relative;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: visible;
  user-select: none;
  width: 100%;

  ${({ height }) =>
    css`
      min-height: ${height}px;
      height: ${height}px;
    `}
`;

const NavigationArrows = styled.div`
  display: flex;
  user-select: none;
  margin-bottom: ${SPACING.L6};
  align-self: flex-end;

  @media (min-width: 768px) {
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  flex: 1 1 100%;
  width: 100%;
`;

const ARROW_SHAPES = {
  RIGHT: (p) =>
    `M${17.74 + p},2 L${33.74 + p},16 L${17.74 + p},32 M${
      31.88 + p
    },16.31 L0,16.31`,
  LEFT: (p) =>
    `M${72 - 17.74 - p},2 L${72 - 33.74 - p},16 L${72 - 17.74 - p},32 M${
      72 - 31.88 - p
    },16.31 L72,16.31`
};

const NavigationArrow = ({
  direction,
  isActive,
  onClick
}: {
  direction: 'left' | 'right',
  isActive: boolean,
  onClick: Function
}) => {
  const { width, color: colorValue } = useSpring({
    color: isActive ? COLORS.GRAY.L8 : COLORS.GRAY.L4,
    width: isActive ? 36 : 0
  });
  const buildPath = ARROW_SHAPES[direction.toUpperCase()];
  const leftMargin = direction === 'right' ? SPACING.L4 : 0;
  const rightMargin = direction === 'left' ? SPACING.L4 : 0;
  return (
    <animated.svg
      onClick={onClick}
      strokeWidth={3}
      style={{
        margin: `0 ${rightMargin} 0 ${leftMargin}`,
        cursor: isActive ? 'pointer' : 'unset'
      }}
      stroke={colorValue}
      width="72px"
      height="34px"
      viewBox="0 0 72 34"
      fill="none"
      fillRule="evenodd"
    >
      <animated.path d={width.interpolate(buildPath)} />
    </animated.svg>
  );
};

export default function Carousel({
  cards,
  title,
  cardDisplays,
  springsRenderer
}: PropsType) {
  const carouselRef = useRef();
  const initialCallbackRef = useRef();
  const cardsLength = cards.length;
  const [selectedIndex, coreSetSelectedIndex] = useState(-1);
  const selectedRef = useRef(selectedIndex);
  const setSelectedIndex = (newIndex) => {
    selectedRef.current = newIndex;
    coreSetSelectedIndex(newIndex);
  };

  const [initialIsAtRightEnd, initialSpringHeight, initialSprings] = getSprings(
    cardDisplays,
    carouselRef.current,
    selectedRef,
    setSelectedIndex,
    cardsLength
  );
  const [springHeight, setSpringHeight] = useState(initialSpringHeight);
  const [springs, setSprings] = useSprings(cardsLength, initialSprings);
  const [isAtRightEnd, setIsAtRightEnd] = useState(initialIsAtRightEnd);
  const updateSprings = (newActiveIndex?: number, gesture?: any) => {
    const [newIsAtRightEnd, newHeight, newSprings] = getSprings(
      cardDisplays,
      carouselRef.current,
      selectedRef,
      setSelectedIndex,
      cardsLength,
      newActiveIndex,
      gesture
    );
    if (newIsAtRightEnd !== isAtRightEnd) setIsAtRightEnd(newIsAtRightEnd);
    if (newHeight !== springHeight) setSpringHeight(newHeight);
    if (newSprings !== springs) setSprings(newSprings);
  };
  const bind = useGesture((gesture) => {
    if (gesture.event && !gesture.event.down && gesture.event.persist) {
      gesture.event.persist();
    }
    updateSprings(undefined, gesture);
  });
  const onResize = useCallback(
    throttle(() => {
      updateSprings();
    }, 300),
    [setSprings, carouselRef.current]
  );
  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);
  const isAtLeftEnd = selectedIndex === 0;
  return (
    <Outer>
      <HeaderRow>
        {title && <Title align="left">{title}</Title>}
        <NavigationArrows>
          <NavigationArrow
            direction="left"
            isActive={!isAtLeftEnd}
            onClick={() => {
              if (isAtLeftEnd) return;
              updateSprings(selectedIndex - 1);
            }}
          />
          <NavigationArrow
            direction="right"
            isActive={!isAtRightEnd}
            onClick={() => {
              if (isAtRightEnd) return;
              updateSprings(selectedIndex + 1);
            }}
          />
        </NavigationArrows>
      </HeaderRow>
      <CarouselWrapper
        height={springHeight}
        ref={(ref) => {
          if (!ref || carouselRef.current === ref) return;
          carouselRef.current = ref;
          if (initialCallbackRef.current) {
            cancelAnimationFrame(initialCallbackRef.current);
          }
          initialCallbackRef.current = requestAnimationFrame(() =>
            updateSprings(0)
          );
        }}
        {...bind()}
      >
        <Wrapper id={carouselId}>{springsRenderer(springs, cards)}</Wrapper>
      </CarouselWrapper>
    </Outer>
  );
}
